import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "../helpers/debounce";
import { getAllInvestors } from "./useAllInvestors";
import { convertToBase64 } from "../helpers/convertToBase64";
import { useInvestmentWalletDetails } from "./useInvestmentWalletDetails";
import {
  updateFormData,
  setSelectedInvestor,
  setTransactionReceipt,
} from "../redux/actions/investmentForm";
import { selectSelectedInvestor } from "../redux/selectors/investmentFormSelectors";
import * as ajaxEndpoints from "../api/ajax-endpoints";
import { getDataWithDotNet } from "../newApis/dotNetApiMethods";

export const useInvestmentFunderSelection = (
  form: any,
  data?: any,
  allInvestmentProducts?: any[],
  handleCurrency?: (currency: string) => void,
) => {
  const dispatch = useDispatch();
  const selectedInvestorInfo = useSelector(selectSelectedInvestor);

  const [investorOptions, setInvestorOptions] = useState<any[]>([]);
  const [wallets, setWallets] = useState<any[]>([]);
  const [investmentProducts, setInvestmentProducts] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [relationshipManagers, setRelationshipManagers] = useState<any[]>([]);
  const [relationshipManagersLoading, setRelationshipManagersLoading] =
    useState<boolean>(false);

  // Track current selected values for wallet API
  const [selectedInvestorId, setSelectedInvestorId] = useState<number | null>(
    null,
  );
  const [selectedProductDetailId, setSelectedProductDetailId] = useState<
    number | null
  >(null);

  // Use the wallet details hook
  const {
    loading: walletLoading,
    error: walletError,
    wallets: apiWallets,
  } = useInvestmentWalletDetails(selectedInvestorId, selectedProductDetailId);

  // Fetch relationship managers on mount
  useEffect(() => {
    const fetchRelationshipManagers = async () => {
      try {
        setRelationshipManagersLoading(true);
        const response = await getDataWithDotNet(
          ajaxEndpoints.GET_WEALTH_MANAGEMENT_RELATIONSHIP_MANAGERS,
        );
        if (response && response.data) {
          setRelationshipManagers(response.data);
        }
      } catch (error) {
        console.error("Error fetching relationship managers:", error);
      } finally {
        setRelationshipManagersLoading(false);
      }
    };

    fetchRelationshipManagers();
  }, []);

  // Initialize investment products
  useEffect(() => {
    if (allInvestmentProducts && allInvestmentProducts.length > 0) {
      setInvestmentProducts(allInvestmentProducts);
    } else if (data?.investment_product_details) {
      setInvestmentProducts([
        {
          id: data.id,
          name: data.name,
          investment_product_details: data.investment_product_details,
        },
      ]);
    }
  }, [allInvestmentProducts, data]);

  // Initialize investor options if we have a selected investor (for persistence)
  useEffect(() => {
    if (
      selectedInvestorInfo &&
      !investorOptions.some(
        (option) => option.value === selectedInvestorInfo.value,
      )
    ) {
      setInvestorOptions((prev) => [
        ...prev,
        {
          value: selectedInvestorInfo.value,
          label: selectedInvestorInfo.label,
          wallets: selectedInvestorInfo.wallets,
        },
      ]);
    }

    // Update investor ID for API when selected investor changes
    if (selectedInvestorInfo?.value) {
      setSelectedInvestorId(Number(selectedInvestorInfo.value));
    }
  }, [selectedInvestorInfo, investorOptions]);

  // Update wallets when API returns data
  useEffect(() => {
    if (apiWallets.length > 0) {
      setWallets(apiWallets);
    }
  }, [apiWallets]);

  // Debounced search for investors
  const fetchInvestors = debounce(async (searchTerm: string) => {
    if (!searchTerm || searchTerm.length < 2) return;

    try {
      setSearchLoading(true);
      const investors = await getAllInvestors({ searchValue: searchTerm });
      const options = investors.map(
        ({
          funder_id,
          wallets,
          customer_name,
          email,
          phone_number,
          customer_type,
          bvn,
          rc_number,
        }: any) => ({
          value: funder_id,
          label: `${customer_name} / ${funder_id}`,
          wallets: wallets,
          email: email || "",
          phoneNumber: phone_number || "",
          customerType: customer_type || "",
          bvn: bvn || "",
          rcNumber: rc_number || "",
        }),
      );

      setInvestorOptions(options);
    } catch (error) {
      console.error("Error fetching investors:", error);
    } finally {
      setSearchLoading(false);
    }
  }, 500);

  // Handle investor selection
  const handleInvestorChange = (selected: any) => {
    // With labelInValue=true, the value is an object: { value, label }
    const investorId = selected?.value;

    // Find the investor in our options
    const selectedInvestor = investorOptions.find(
      (investor) => investor.value === investorId,
    );

    if (!selectedInvestor) {
      return;
    }

    // Update selected investor ID for wallet API
    setSelectedInvestorId(Number(investorId));

    // We'll rely on the API to get wallet data now, but as a fallback:
    if (selectedInvestor.wallets) {
      const formattedWallets = selectedInvestor.wallets.map(
        (wallet: any, index: number) => ({
          key: index,
          legalEntity: "Advancly Tech",
          accountNumber:
            wallet.accountNumber ||
            wallet.walletId ||
            Math.floor(Math.random() * 10000000000).toString(),
          accountName: `${selectedInvestor.label.split(" /")[0]}`,
          currency: wallet.currency,
          amount: wallet.availableBalance || 0,
          walletId: wallet.walletId,
        }),
      );

      // Store selected investor info in Redux with formatted wallets as fallback
      dispatch(
        setSelectedInvestor({
          value: selectedInvestor.value,
          label: selectedInvestor.label,
          wallets: formattedWallets,
        }),
      );
    } else {
      // Store selected investor info in Redux without wallets
      dispatch(
        setSelectedInvestor({
          value: selectedInvestor.value,
          label: selectedInvestor.label,
          wallets: [],
        }),
      );
    }

    // Update Redux state
    dispatch(
      updateFormData({
        funder_id: investorId,
        raw_funder_id: investorId,
      }),
    );

    // Store the numeric funder_id value directly in the form
    form.setFieldsValue({
      raw_funder_id: investorId,
    });

    // Also set it on the parent's state object if available
    if (form.setFieldValue) {
      // For newer Ant Design versions
      form.setFieldValue("raw_funder_id", investorId);
    }
  };

  // Handle currency change
  const handleCurrencyChange = (value: string) => {
    // Update Redux state
    dispatch(
      updateFormData({
        currency: value,
      }),
    );

    // Call the legacy handler for backward compatibility
    if (handleCurrency) {
      handleCurrency(value);
    }
  };

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    // Check file type
    const fileType = file.type;
    const isValidFile =
      fileType.startsWith("image/png") ||
      fileType.startsWith("image/jpg") ||
      fileType.startsWith("image/jpeg") ||
      fileType.startsWith("application/pdf");

    if (!isValidFile) {
      return false;
    }

    // Convert to base64
    try {
      const fileBase64 = await convertToBase64(file);

      // Update Redux state with transaction receipt
      dispatch(
        setTransactionReceipt({
          base64: fileBase64 as string,
          fileName: file.name,
        }),
      );
    } catch (error) {
      console.error("Error converting file to base64:", error);
    }

    // Return false to prevent default upload behavior
    return false;
  };

  // Listen for investment_product_detail_id in Redux
  useEffect(() => {
    // Create a timer to continuously check for investment_product_detail_id
    const timer = setInterval(() => {
      try {
        const formData = form.getFieldsValue();

        // Check direct investment_product_detail_id in Redux store
        if (formData.investment_product_detail_id) {
          setSelectedProductDetailId(
            Number(formData.investment_product_detail_id),
          );
          clearInterval(timer);
          return;
        }

        // Try to extract from the investment_product_id if it's in labelInValue format
        if (
          formData.investment_product_id &&
          typeof formData.investment_product_id.value === "string"
        ) {
          const value = formData.investment_product_id.value;
          if (value.includes("-")) {
            const [productId, currencyCode] = value.split("-");

            // Find product detail ID for the selected product and currency
            let foundDetailId = null;

            // Check in current data first
            if (data?.investment_product_details) {
              const detail = data.investment_product_details.find(
                (d: any) => d.currency_code === currencyCode,
              );
              if (detail?.id) {
                foundDetailId = detail.id;
              }
            }

            // If not found, check in all products
            if (!foundDetailId && investmentProducts.length > 0) {
              const product = investmentProducts.find(
                (p) => p.id.toString() === productId,
              );
              if (product?.investment_product_details) {
                const detail = product.investment_product_details.find(
                  (d: any) => d.currency_code === currencyCode,
                );
                if (detail?.id) {
                  foundDetailId = detail.id;
                }
              }
            }

            if (foundDetailId) {
              setSelectedProductDetailId(foundDetailId);
              clearInterval(timer);
            }
          }
        }
      } catch (error) {
        console.error("Error checking for product detail ID:", error);
      }
    }, 500);

    // Cleanup timer
    return () => clearInterval(timer);
  }, [form, data, investmentProducts]);

  // Handle relationship manager selection
  const handleRelationshipManagerChange = (value: number) => {
    dispatch(
      updateFormData({
        relationship_manager_id: value,
      }),
    );
  };

  return {
    investorOptions,
    wallets,
    investmentProducts,
    searchLoading,
    selectedInvestorInfo,
    relationshipManagers,
    relationshipManagersLoading,
    fetchInvestors,
    handleInvestorChange,
    handleCurrencyChange,
    handleFileUpload,
    handleRelationshipManagerChange,
    walletLoading,
  };
};
